/**
 * What a rep is actually paid on a deal (Monday 12953417291).
 *
 * Deliberately dependency-free, like `categoryVisibility.ts` and
 * `contrast.ts`. Every other module under `common/helpers` pulls in `../env`,
 * which reads REACT_APP_COMMON_ENV at import time and throws without it, so
 * anything living there cannot be unit tested. This is someone's pay - it gets
 * to be tested.
 *
 * `schema.ts` imports THIS FILE directly rather than through the barrel, so
 * the threshold a new deal is stamped with and the threshold the screens read
 * are the same constant. Importing the barrel would pull in `helpers.ts`,
 * which imports schema, and close a cycle. This module imports nothing.
 *
 * THE RULE
 * A rep keeps their commission in full up to a threshold, and 75% of anything
 * above it. Shin (accounting) asked for that threshold to drop from $1,250 to
 * $750 - first proposing $500, then settling on $750.
 *
 * WHY THE THRESHOLD IS STAMPED ON THE DEAL RATHER THAN READ AS A CONSTANT
 * Shin, on when the new number applies: "To new deals moving forward since
 * reps already knew their commission for their sales." The commission is
 * worked out when a deal is RENDERED, not when it was sold, so a bare constant
 * would silently rewrite the payout on every past deal the moment anyone
 * reopened it - a rep who sold in August under the old rule would find a
 * different figure on their own closed deal.
 *
 * Anchoring to a date instead would force a choice between the creation, sale
 * and signature dates, which do not coincide, and would leave the figure
 * depending on a cut-off that has to be edited again next time. Stamping means
 * each deal permanently carries the rule it was sold under.
 *
 * Measured before building: all 7,151 existing customers have no stamped
 * value, so every one of them falls back to $1,250 and nothing already sold
 * moves. Two carry the `commission_no_split` exemption and are untouched by
 * any of this.
 */

/** What a deal created from now on is stamped with. */
export const COMMISSION_SPLIT_THRESHOLD = 750

/**
 * What a deal with no stamped threshold is worth. Every customer that existed
 * before this field did lands here, which is the whole guarantee: nothing
 * already sold changes.
 */
export const LEGACY_COMMISSION_SPLIT_THRESHOLD = 1250

/** The rep's share of everything above the threshold. */
export const COMMISSION_SPLIT_RATE = 0.75

/**
 * Arizona-only rule Jaron confirmed 2026-09-25: new deals start the split at
 * $500 instead of $750, and the lead fee is $1,200 instead of $1,600.
 * `getCompanyState` returns the lowercase name from COMPANY_STATES.
 */
export const ARIZONA_STATE = 'arizona'
export const ARIZONA_NEW_SPLIT_THRESHOLD = 500
export const ARIZONA_NEW_LEAD_FEE = 1200

/**
 * What gets written when a deal is born, on the app AND the web. A new Arizona
 * deal gets $500 / $1,200; every other new deal gets COMMISSION_SPLIT_THRESHOLD
 * and a lead-fee stamp of 0 (zero = "read the live fee").
 *
 * The threshold is NOT a parameter on purpose. It used to be, and the web
 * passed `createEmptyCustomer(...).commission_split_threshold`, which is 0 by
 * design (the template must not stamp legacy reads). 0 reads as "sold before
 * the field existed", so every new non-Arizona web deal from 2026-09-09 to
 * 2026-10-06 was paid at the legacy $1,250 (B166). A deal being born always
 * gets the current rule; there is no caller that should choose otherwise.
 * `dealBirthStamp.test.ts` fails if a file that creates a customer record
 * does not call this. This function never rewrites a stored deal.
 */
export const stampsForNewDeal = (
  state: string,
): { commission_split_threshold: number; lead_fee_stamped: number } => {
  if (state === ARIZONA_STATE) {
    return {
      commission_split_threshold: ARIZONA_NEW_SPLIT_THRESHOLD,
      lead_fee_stamped: ARIZONA_NEW_LEAD_FEE,
    }
  }
  return {
    commission_split_threshold: COMMISSION_SPLIT_THRESHOLD,
    lead_fee_stamped: 0,
  }
}

/**
 * THE RULE BY BIRTH DATE (B166, 2026-10-06)
 *
 * Which threshold and lead fee a deal gets is decided from WHEN IT WAS BORN,
 * read from its Firebase push key, which encodes the creation time in
 * milliseconds and never changes. Not from a stamp the client writes.
 *
 * The stamp approach failed three times in a month: old app builds on reps'
 * phones did not write it (Sep 3-11), the emergency fix moved it off the
 * template and the web stopped writing it (Sep 15 - Oct 6), and every miss was
 * silent because "no stamp" meant "legacy". A birth date cannot be forgotten
 * by a client, an old build or a new screen.
 *
 * The only per-deal exception is `commission_rules_date`: "this deal follows
 * the rules of day X" instead of its birth day. It moves the split and the
 * lead fee together and cannot invent a number - keeping a deal as it was is a
 * date before the cutoff, and a test deal follows today's rules with today's
 * date (switching states then shows each state's numbers).
 * `commission_no_split` (rep keeps everything, in `repCommission`) is a
 * different thing and wins over all of it.
 *
 * The stamps are still written at birth so old app builds keep showing new
 * deals right, but nothing here reads them.
 */
/**
 * Midnight Sep 3 2026 in Nevada/Arizona (UTC-7): the day the $750 threshold
 * took effect for new deals (12953417291). Deals born before it keep $1,250.
 */
export const SPLIT_RULE_FROM = '2026-09-03T07:00:00.000Z'
/**
 * Midnight Sep 26 2026 in Arizona (UTC-7). Jaron confirmed the Arizona $500
 * split / $1,200 lead fee on Sep 25 for NEW deals, and it went live that
 * afternoon. A deal opened on Sep 25 before the release existed already and
 * keeps $1,600 (the lead fee is part of the job's price), so the first full
 * day live is the cutoff.
 */
export const ARIZONA_RULE_FROM = '2026-09-26T07:00:00.000Z'
/**
 * Shin, Oct 12 2026: "No need to change previous sold deals because those
 * were the commission reps expected to get. Just make sure it's updated now
 * and new sales will have that formula." The birth-date rule went live at this
 * time; every deal created before it keeps exactly what it showed before the
 * release (its stamp, or the legacy rule when it has none). Only deals created
 * from here on are decided by their birth date.
 */
export const BIRTH_DATE_RULE_LIVE_FROM = '2026-10-12T15:44:00.000Z'
const BIRTH_DATE_RULE_LIVE_FROM_MS = Date.parse(BIRTH_DATE_RULE_LIVE_FROM)
const SPLIT_RULE_FROM_MS = Date.parse(SPLIT_RULE_FROM)
const ARIZONA_RULE_FROM_MS = Date.parse(ARIZONA_RULE_FROM)

const PUSH_CHARS =
  '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz'

/** Push keys did not exist before this; anything earlier is not one. */
const PUSH_KEYS_FROM_MS = Date.UTC(2012, 0, 1)

/**
 * Creation time (ms) encoded in the first 8 characters of a Firebase push
 * key, or NaN when the key is not a push key. A push key is exactly 20
 * characters and dates between 2012 and now (+1 day of clock skew); a key
 * like `customer-1` uses the same alphabet, so the range is what rejects it.
 */
export const pushKeyTime = (key: unknown): number => {
  if (typeof key !== 'string' || key.length !== 20) return NaN
  let t = 0
  for (let i = 0; i < 8; i++) {
    const v = PUSH_CHARS.indexOf(key.charAt(i))
    if (v < 0) return NaN
    t = t * 64 + v
  }
  return t >= PUSH_KEYS_FROM_MS && t <= Date.now() + 864e5 ? t : NaN
}

export interface DealRuleInput {
  /** The deal's Firebase key. */
  key: unknown
  /** `getCompanyState(solarCompany)`, lowercase; '' when unknown. */
  state: string
  /**
   * `commission_rules_date` (ms): the date whose rules this deal follows
   * instead of its birth date. Only a positive number counts.
   */
  rulesDate?: unknown
  /** `commission_split_threshold` as stored (deals created before the rule). */
  stampedThreshold?: unknown
  /** `lead_fee_stamped` as stored (deals created before the rule). */
  stampedLeadFee?: unknown
}

/**
 * True for a deal created before the birth-date rule went live and without a
 * rules date: it keeps what it had (Shin, Oct 12).
 */
const keepsPreviousRules = (deal: DealRuleInput): boolean => {
  if (isPositive(deal.rulesDate)) return false
  const born = pushKeyTime(deal.key)
  return !Number.isFinite(born) || born < BIRTH_DATE_RULE_LIVE_FROM_MS
}

const isPositive = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0

/** The date a deal's rules come from: its rules date if set, else its birth. */
export const dealRulesTime = ({
  key,
  rulesDate,
}: Pick<DealRuleInput, 'key' | 'rulesDate'>): number =>
  isPositive(rulesDate) ? rulesDate : pushKeyTime(key)

const onOrAfter = (deal: DealRuleInput, fromMs: number): boolean => {
  const t = dealRulesTime(deal)
  return Number.isFinite(t) && t >= fromMs
}

/**
 * The split threshold of one deal. A deal that cannot be dated (key not a
 * push key, no rules date) stays on the legacy rule, like everything sold
 * before the cutoff.
 */
export const splitThresholdForDeal = (deal: DealRuleInput): number => {
  if (keepsPreviousRules(deal)) {
    return commissionSplitThreshold(deal.stampedThreshold)
  }
  if (!onOrAfter(deal, SPLIT_RULE_FROM_MS)) {
    return LEGACY_COMMISSION_SPLIT_THRESHOLD
  }
  if (deal.state === ARIZONA_STATE && onOrAfter(deal, ARIZONA_RULE_FROM_MS)) {
    return ARIZONA_NEW_SPLIT_THRESHOLD
  }
  return COMMISSION_SPLIT_THRESHOLD
}

/**
 * The lead-fee stamp equivalent for one deal: $1,200 for an Arizona deal born
 * on or after the Arizona cutoff, else 0 (= use the live `stateToLeadFee`).
 * Feed it to `leadFeeForDeal`.
 */
export const leadFeeStampForDeal = (deal: DealRuleInput): number => {
  if (keepsPreviousRules(deal)) {
    const s = deal.stampedLeadFee
    return typeof s === 'number' && Number.isFinite(s) && s > 0 ? s : 0
  }
  return deal.state === ARIZONA_STATE && onOrAfter(deal, ARIZONA_RULE_FROM_MS)
    ? ARIZONA_NEW_LEAD_FEE
    : 0
}

/**
 * Lead fee actually added to a deal. A positive stamp wins. Absent, zero and
 * junk all mean "this deal was not stamped", so the live env fee is used.
 * Leaving `stateToLeadFee` at $1,600 is what keeps every current Arizona job,
 * locked or not, on $1,600.
 */
export const leadFeeForDeal = (stamped: unknown, liveFee: number): number => {
  if (typeof stamped === 'number' && Number.isFinite(stamped) && stamped > 0) {
    return stamped
  }
  return typeof liveFee === 'number' && Number.isFinite(liveFee) ? liveFee : 0
}

/**
 * The threshold that applies to one deal.
 *
 * Absent, zero and anything non-finite all mean "sold before this existed".
 * Zero matters specifically: the schema's default for a number field is 0, so
 * a customer read from Firebase without the key arrives as 0, not undefined.
 * Reading that literally would hand the rep 75% of every dollar they earn.
 */
export const commissionSplitThreshold = (stamped: unknown): number =>
  typeof stamped === 'number' && Number.isFinite(stamped) && stamped > 0
    ? stamped
    : LEGACY_COMMISSION_SPLIT_THRESHOLD

/**
 * What the rep is paid, given the deal's raw commission.
 *
 * `noSplit` is the per-deal exemption (Jaron, Slack, Jul 2026): the rep keeps
 * the whole amount regardless. It is not a threshold change and is unaffected
 * by any of the above.
 *
 * Lives here rather than inline at each screen because it was written out
 * three times - the web customer page, the app's Payment screen and the app's
 * Customer Info - each with its own copy of the number. Changing the threshold
 * meant editing three files and hoping none was missed, which is V62's whole
 * point.
 */
export const repCommission = (
  finalCommission: number,
  stampedThreshold: unknown,
  noSplit: boolean,
): number => {
  // A payment snapshot saved by the old NaN Apply holds finalCommission null
  // (JSON turns NaN into null). Every caller does .toFixed() on the result, so
  // passing null through took the whole customer page down (Teresa Nolasco,
  // Oct 19). Not a number in, NaN out: the field reads "NaN", the page stays up.
  if (typeof finalCommission !== 'number' || !Number.isFinite(finalCommission))
    return Number.NaN
  if (noSplit) return finalCommission

  const threshold = commissionSplitThreshold(stampedThreshold)

  return finalCommission >= threshold
    ? threshold + (finalCommission - threshold) * COMMISSION_SPLIT_RATE
    : finalCommission
}
